// useWhipEncodingSettings.ts — Réglages d'encodage WHIP de la chaîne affichée.
//
// Lit et mémorise les réglages (cf. whipEncodingSettings.ts), et les publie comme
// réglages en vigueur : api_whip les lit au démarrage et applique à chaud chaque
// changement à la diffusion en cours.

import { useCallback, useEffect, useState } from 'react';
import {
  WhipEncodingSettings, loadWhipEncoding, sanitizeWhipEncoding, saveWhipEncoding,
  setActiveWhipEncoding, whipEncodingStorageKey, WHIP_ENCODING_RESTORED_EVENT,
} from '../../../dataTypes/liveStreamManagment/whipEncodingSettings';

export function useWhipEncodingSettings(channelId: number | null) {
  const key = whipEncodingStorageKey(channelId);
  const [settings, setSettingsState] = useState<WhipEncodingSettings>(() => loadWhipEncoding(key));

  // L'identifiant de la chaîne est souvent inconnu au montage : à chaque changement
  // de clé, on relit les réglages de CETTE chaîne.
  useEffect(() => { setSettingsState(loadWhipEncoding(key)); }, [key]);
  useEffect(() => { setActiveWhipEncoding(settings); }, [settings]);
  // Chargement d'un deck : les réglages ont été réécrits en mémoire, on les relit.
  useEffect(() => {
    const onRestored = (e: Event) => {
      if ((e as CustomEvent<{ key: string }>).detail?.key === key) setSettingsState(loadWhipEncoding(key));
    };
    window.addEventListener(WHIP_ENCODING_RESTORED_EVENT, onRestored);
    return () => window.removeEventListener(WHIP_ENCODING_RESTORED_EVENT, onRestored);
  }, [key]);

  const setSettings = useCallback((next: WhipEncodingSettings) => {
    const clean = sanitizeWhipEncoding(next);
    setSettingsState(clean);
    saveWhipEncoding(key, clean);
  }, [key]);

  return { settings, setSettings };
}
